import { createRouter, createWebHistory } from 'vue-router'
import { setLocale, getCurrentLocale } from '@/plugins/i18n'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { loadMetaPixel } from '@/composables/useLoadScripts'
import { useMediaStore } from '@/store'

// the only admin page a partner half-admin may open
const SUB_ADMIN_ROUTES = [{ name: 'AllUsers', permission: 'canAccessAllUsers' }]

function getSubAdminHomeRoute(authStore) {
  return SUB_ADMIN_ROUTES.find(({ permission }) => authStore[permission])?.name
}

/**
 * Sub admins may only open the admin pages they were granted.
 * @returns a redirect target when the page is not allowed, otherwise null
 */
function resolveAdminAreaRedirect(to) {
  const authStore = useAuthStore()
  if (authStore.isAdmin) return null

  const userDashboard = `/${getCurrentLocale()}/dashboard`
  if (!authStore.isSubAdmin) return userDashboard

  const { permission } = to.meta
  if (permission && authStore[permission]) return null

  const home = getSubAdminHomeRoute(authStore)
  return home && home !== to.name ? { name: home } : userDashboard
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // login route
    {
      path: '/:lang/login',
      name: 'Login',
      component: () => import('@/views/Login.vue'),
    },
    // admin routes
    {
      path: '/admin',
      component: () => import('@/views/Admin/AdminRoot.vue'),
      meta: { adminArea: true },
      beforeEnter: async (to, from, next) => {
        const authStore = useAuthStore()
        if (authStore.isAdmin || authStore.isSubAdmin) {
          next()
        } else if (authStore.isAuth) {
          next(`/${getCurrentLocale()}/dashboard`)
        } else {
          next({ name: 'Login' })
        }
      },
      children: [
        {
          path: '',
          redirect: () => '/admin/dashboard',
        },
        {
          path: 'dashboard',
          name: 'AdminDashboard',
          component: () => import('@/views/Admin/AdminDashboard.vue'),
        },
        {
          path: 'all-users',
          name: 'AllUsers',
          component: () => import('@/views/Admin/AllUsers.vue'),
          meta: { permission: 'canAccessAllUsers' },
        },
        {
          path: 'all-question',
          name: 'AllQuestions',
          component: () => import('@/views/Admin/AllQuestions.vue'),
        },
        {
          path: 'all-games',
          name: 'AllGames',
          component: () => import('@/views/Admin/AllGames.vue'),
          meta: { permission: 'canManageAllGames' },
        },
        {
          path: 'edit-subscriptions',
          name: 'EditSubscriptions',
          component: () => import('@/views/Admin/EditSubscriptions.vue'),
        },
        {
          path: 'institution',
          name: 'Institution',
          component: () => import('@/views/Admin/Institution.vue'),
        },
        {
          path: 'partners',
          name: 'Partners',
          component: () => import('@/views/Admin/Partners.vue'),
        },
        {
          path: 'blacklist',
          name: 'AdminBlacklist',
          component: () => import('@/views/Admin/BlacklistEmails.vue'),
        },
        {
          path: 'coupons',
          name: 'AdminCoupons',
          component: () => import('@/views/Admin/Coupons.vue'),
        },
        {
          path: 'pulse',
          name: 'PulseDiagnostics',
          component: () => import('@/views/Admin/PulseDiagnostics.vue'),
        },
        {
          path: 'job-runs',
          name: 'JobRuns',
          component: () => import('@/views/Admin/JobRuns.vue'),
        },
      ],
    },
    // user routes
    {
      path: '/:lang',
      beforeEnter: (to, from, next) => {
        const authStore = useAuthStore()
        if (authStore.isAuth) next()
        else next({ name: 'Login' })
      },
      children: [
        {
          path: 'dashboard',
          name: 'UserDashboard',
          component: () => import('@/views/User/HomePage.vue'),
        },
        {
          path: 'create-game/:id/:queIndex?',
          name: 'CreateGame',
          component: () => import('@/views/User/GameEditor.vue'),
        },
        {
          path: 'payment-success',
          name: 'PaymentSuccess',
          component: () => import('@/views/PaymentSuccess.vue'),
        },
        {
          path: 'user-settings',
          name: 'UserSettings',
          component: () => import('@/views/User/UserSettings.vue'),
        },
      ],
    },
    {
      path: '/loading',
      name: 'Loading',
      component: () => import('@/views/AuthLoading.vue'),
    },
    {
      path: '/notfound-edu',
      name: 'NotFoundEdu',
      component: () => import('@/views/NotFoundEdu.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      redirect: () => `/${getCurrentLocale()}/login`,
    },
  ],
})

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  } else {
    console.error(err)
  }
})

const FAST_LOADER = ['Login']
router.isReady().then(async () => {
  localStorage.removeItem('vuetify:dynamic-reload')
  const { setLoadingActive } = useAppStore()
  if (FAST_LOADER.includes(router.currentRoute.value.name)) {
    setLoadingActive(false)
  } else {
    setTimeout(() => {
      setLoadingActive(false)
    }, 1500)
  }
  if (router.currentRoute.value.query.gallery) {
    const { setGalleryOpen } = useMediaStore()
    setGalleryOpen(true)
  }
})

const isNotAuthRoutes = ['Login', 'NotFound', 'NotFoundEdu', 'Loading']
router.beforeEach(async (to, from, next) => {
  const { isAuth, checked } = useAuthStore()
  if (to.name !== 'Loading' && !checked) {
    return next({ path: '/loading', query: { redirect: to.fullPath } })
  }

  if (!isNotAuthRoutes.includes(to.name) && !isAuth) {
    return next({ name: 'Login' })
  }

  if (to.name === 'Login' && isAuth) {
    return next(`/${getCurrentLocale()}/dashboard`)
  }

  if (to.matched.some(record => record.meta.adminArea)) {
    const redirect = resolveAdminAreaRedirect(to)
    if (redirect) return next(redirect)
  }

  if (to.fullPath === '/?isNewUser=1') {
    loadMetaPixel({
      eventType: 'CompleteRegistration',
    })
  }

  next()
})

export default router
